import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, AlertTriangle, XCircle } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import { adminFetch } from '../utils/adminFetch';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { checkAdmin } = useAdmin();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !password) {
      setError({ message: 'Completa todos los campos' });
      return;
    }

    setLoading(true);
    try {
      const response = await adminFetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      // Clone response to prevent "body stream already read" from monitoring scripts
      const data = await response.clone().json();
      
      if (!data.success) {
        setError({
          message: data.error || 'Error de autenticación',
          remainingAttempts: data.remaining_attempts,
          locked: data.locked
        });
        return;
      }

      // Store token in sessionStorage for Authorization header fallback
      if (data.token) {
        sessionStorage.setItem('admin_token', data.token);
      }
      sessionStorage.setItem('admin_email', data.email);

      // Refresh admin state from context
      await checkAdmin();
      toast.success('Bienvenido al panel de administración');
      navigate('/admin');
    } catch (err) {
      console.error('Login error:', err);
      setError({ message: 'Error de conexión. Inténtalo de nuevo.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-foreground text-background rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8" />
          </div>
          <h1 className="font-serif text-3xl mb-2">Panel de Administración</h1>
          <p className="text-muted-foreground">Jon Quintano Fotografía</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-card border border-border p-8">
          {/* Error message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg flex items-start gap-3 ${
                error.locked 
                  ? 'bg-red-500/10 border border-red-500/30' 
                  : 'bg-amber-500/10 border border-amber-500/30'
              }`}
              data-testid="login-error-message"
            >
              {error.locked ? (
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`font-medium ${error.locked ? 'text-red-500' : 'text-amber-500'}`}>
                  {error.message}
                </p>
                {error.remainingAttempts !== undefined && error.remainingAttempts > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Te quedan <span className="font-semibold text-amber-500">{error.remainingAttempts}</span> {error.remainingAttempts === 1 ? 'intento' : 'intentos'} antes de bloquear la cuenta.
                  </p>
                )}
              </div>
            </motion.div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                placeholder="admin@ejemplo.com"
                data-testid="admin-email"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                placeholder="••••••••"
                data-testid="admin-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-foreground text-background hover:bg-foreground/90"
            data-testid="admin-submit"
          >
            {loading ? 'Accediendo...' : 'Acceder'}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-muted-foreground">
          <a href="/" className="hover:text-foreground transition-colors">
            ← Volver a la web
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
